import { defineStore } from 'pinia'
import { useSearchQueryStore } from "@/store/SearchQueryStore";
import { useSuggestionStore } from "@/store/AutocompletePopup/SuggestionStore";
import { useAutocompleteAggregationsStore } from "@/store/AutocompletePopup/AutocompleteAggregationsStore";
import { config } from "@/core/config";
import { SanitizeValue } from "@/core/utils";
import type { ProductInterface } from "@/core/types";
import { SmartSearchConfig } from "@/main";
import { searchErrorLogger } from "@/core/Logger";
import { useBridgeStore } from "@/store/BridgeStore";
import { useMobileViewStore } from "@/store/MobileViewStore";

const safeElements = new WeakSet<HTMLElement>();
let outsidePointerHandler: ((event: Event) => void) | null = null;
const POINTER_LISTENER_CAPTURE = true;

function isInsideSafeElement(event: Event): boolean {
  if (!(event.target instanceof Node)) {
    return false;
  }

  let node: Node | null = event.target;
  while (node) {
    if (node instanceof HTMLElement && safeElements.has(node)) {
      return true;
    }
    node = node.parentNode;
  }

  return false;
}

function isAbortError(err: any): boolean {
  return err?.name === 'AbortError' || err?.code === 20;
}

export const usePopupStore = defineStore('PopupStore', {
  state: () => ({
    showAutocompletePopup: false,
    showResults: false,
    results: [] as Array<ProductInterface>,
    total: 0,
    typingTimer: 0 as any,
    loading: false,
    productsController: null as AbortController | null,
    lastQuery: '',
    
    bestseller: null as ProductInterface | null,
    
    requestSeq: 0,
    lastAppliedReq: 0,
  }),
  
  getters: {
    hasAnyResults(state): boolean {
      return state.results.length > 0
    },
    getTotalProducts(state): number {
      return state.total
    },
    hasNoResults(state): boolean {
      return state.results.length === 0
    },
    canShowPromotedSection(state): boolean {
      return state.total >= SmartSearchConfig.value.autocomplete.results.count
    },
    getBestseller(state): ProductInterface | null {
      return state.bestseller
    },
    getAutocompleteResultsWithoutFirst(state): Array<ProductInterface> {
      return state.results.slice(1, SmartSearchConfig.value.autocomplete.results.count + 1)
    },
    getAutocompleteResults(state): Array<ProductInterface> {
      return state.results.slice(0, SmartSearchConfig.value.autocomplete.results.count)
    }
  },
  
  actions: {
    registerSafeElement(element?: HTMLElement | null): () => void {
      if (!element) {
        return () => {
          /* no-op */
        };
      }
      safeElements.add(element);
      return () => {
        safeElements.delete(element);
      };
    },
    attachOutsidePointerListener(): void {
      if (outsidePointerHandler || typeof window === 'undefined') {
        return;
      }
      outsidePointerHandler = (event: Event) => {
        if (isInsideSafeElement(event)) {
          return;
        }
        this.hideAutocomplete();
      };
      window.addEventListener('pointerdown', outsidePointerHandler, POINTER_LISTENER_CAPTURE);
    },
    detachOutsidePointerListener(): void {
      if (!outsidePointerHandler || typeof window === 'undefined') {
        return;
      }
      window.removeEventListener('pointerdown', outsidePointerHandler, POINTER_LISTENER_CAPTURE);
      outsidePointerHandler = null;
    },
    setBodyActiveAutocompleteClass(set: boolean) {
      const cls = 'q-autocomplete-active';
      if (set) {
        document.body.classList.add(cls);
        document.documentElement.classList.add(cls);
      } else {
        document.body.classList.remove(cls);
        document.documentElement.classList.remove(cls);
      }
    },
    
    showAutocomplete() {
      const bridgeStore = useBridgeStore();
      const mobileViewStore = useMobileViewStore();
      const queryStore = useSearchQueryStore();
      const suggestionStore = useSuggestionStore();
      const autocompleteAggregationsStore = useAutocompleteAggregationsStore();
      
      this.showAutocompletePopup = true;
      this.setBodyActiveAutocompleteClass(true);
      this.attachOutsidePointerListener();
      bridgeStore.activateWrapper();
      bridgeStore.updatePopupPlacement();
      
      if (mobileViewStore.isMobileView) bridgeStore.focusInternalInput();
      else bridgeStore.focusClientInput();
      
      const trimmedQuery = queryStore.searchQuery.trim();
      this.lastQuery = trimmedQuery;
      
      if (trimmedQuery.length > 2) {
        if (this.results.length) this.showResults = true;
        else void this.fetchAutocompleteResults();
        
        if (suggestionStore.suggestions.length || suggestionStore.newSuggestions.length) {
          suggestionStore.showSuggestions = true;
        } else {
          void suggestionStore.fetchNewSuggestions();
        }

        void autocompleteAggregationsStore.fetchAggregationsForCurrentQuery();
      } else {
        // <3 znaki -> widok OnFocus
        this.showResults = false;
      }
    },
    
    hideAutocomplete() {
      const bridgeStore = useBridgeStore();
      this.showAutocompletePopup = false;
      this.setBodyActiveAutocompleteClass(false);
      bridgeStore.deactivateWrapper();
      this.detachOutsidePointerListener();
    },
    
    clearBestseller() {
      this.bestseller = null;
    },
    
    handleKeyUp() {
      const queryStore = useSearchQueryStore();
      const suggestionStore = useSuggestionStore();
      const autocompleteAggregationsStore = useAutocompleteAggregationsStore();
      
      if (queryStore.searchQuery.length <= 2) {
        suggestionStore.showSuggestions = false;
        this.showResults = false;
        if (this.typingTimer) clearTimeout(this.typingTimer);
        if (this.productsController) this.productsController.abort();
        this.loading = false;
        return;
      }
      
      if (this.typingTimer) clearTimeout(this.typingTimer);
      
      this.typingTimer = setTimeout(async () => {
        try {
          this.loading = true;
          await Promise.all([
            suggestionStore.fetchNewSuggestions(),
            this.fetchAutocompleteResults(),
            autocompleteAggregationsStore.fetchAggregationsForCurrentQuery()
          ]);
        } finally {
          // domykane w fetchu (with abort guard)
        }
      }, 300);
    },
    
    async fetchAutocompleteResults() {
      const queryStore = useSearchQueryStore();
      if (queryStore.searchQuery.length < 3) return;
      
      if (this.productsController) this.productsController.abort();
      this.productsController = new AbortController();
      const { signal } = this.productsController;
      
      const form = new FormData();
      const trimmedQuery = queryStore.searchQuery.trim();
      if (trimmedQuery) {
        this.lastQuery = trimmedQuery;
      }
      const sanitizedQuery = SanitizeValue(queryStore.searchQuery);
      form.append('q', sanitizedQuery);
      
      const myReq = ++this.requestSeq;
      
      try {
        this.loading = true;
        
        const response = await fetch(
          `https://${config.apiHost}/restapi/${config.customerSymbol}/search/${config.searchId}/products`,
          {
            method: 'POST',
            headers: { 'Api-key': config.apiKey },
            credentials: 'include',
            body: form,
            signal,
          }
        );
        
        if (!response.ok) return;
        const payload = await response.json();
        
        if (myReq < this.requestSeq) return; // stara odpowiedź
        
        const { products = [], meta = { total: 0 } } = payload;
        
        this.clearBestseller();
        this.total = meta?.total ?? 0;
        this.results = Array.isArray(products) ? products : [];
        this.bestseller = this.results[0] ?? null;

        this.showResults = true;
        this.lastAppliedReq = myReq;
        
      } catch (error: any) {
        if (isAbortError(error)) return;
        this.showResults = false;
        this.loading = false;
        searchErrorLogger.log('Error retrieving search results:', error);
      } finally {
        if (!(this.productsController && this.productsController.signal?.aborted)) {
          this.loading = false;
        }
      }
    },
    
    handleClickOutside(event: Event) {
      const queryStore = useSearchQueryStore();
      
      const targetEl = event.target as Element | null;
      const targetClasses = targetEl instanceof Element ? Array.from(targetEl.classList) : [];
      const trimmedQuery = queryStore.searchQuery.trim();
      
      // 1) klasy wykluczeń (slidery, strzałki itd.)
      const defaultExcludedClasses = [
        'slick-arrow','swiper-button-next','swiper-button-prev',
        'glide__arrow','splide__arrow','tns-controls',
        'flickity-prev-next-button','owl-nav','owl-dot',
        'slider-nav-left','slider-nav-right',
        'autocomplete-search__input','q-form-control',
        'autocomplete-search__input-max-width',
        'autocomplete-popup__input-container','q-input-group', 'q-icon--gmail-x'
      ];
      if (trimmedQuery.length === 0) {
        defaultExcludedClasses.push('autocomplete-search__input-button')
      }
      
      const additionalExcludedClasses = (window as any)._QONizer?.customSmartSearchConfig?.excludedClasses || [];
      const allExcludedClasses = [...defaultExcludedClasses, ...additionalExcludedClasses];
      const isExcludedByClass = targetClasses.some(c => allExcludedClasses.includes(c));
      
      const searchInput = document.getElementById('autocomplete-search__input');
      const autocompletePopup = document.getElementById('smartsearch-autocomplete__popup-container');
      
      const bridgeStore = useBridgeStore();
      const clientInput = bridgeStore.clientInput;           // realny input klienta (nie wrapper)
      const clientWrapper = bridgeStore.clientInputWrapper;  // UWAGA: duży kontener!
      
      // helper: czy klik jest w danym węźle (bezpiecznie)
      const contains = (node: Element | null | undefined, target: EventTarget | null): boolean =>
        !!(node && target && node.contains(target as Node));
      
      // 2) jeśli nie mamy kluczowych węzłów — wyjdź
      if (!autocompletePopup) return;
      
      // 3) jeśli klik trafił w elementy, których NIE traktujemy jako “poza”
      const isInPopup = contains(autocompletePopup, event.target);
      const isInSearchInput = contains(searchInput, event.target);
      const isInClientInput = contains(clientInput as Element | null, event.target);
      
      // 5) Early returns - klik wewnątrz dozwolonych obszarów, albo na klasach wykluczonych
      if (targetClasses.length === 0) return;                 // np. strzałki sliderów
      if (isExcludedByClass) return;                 // np. strzałki sliderów
      if (isInPopup) return;                         // klik wewnątrz popupu
      if (isInSearchInput) return;                   // klik w nasz input
      if (isInClientInput) return;                   // klik w input klienta (zbridge’owany)
      
      // 6) IMPORTANT: NIE ignorujemy całego wrappera!
      //    Klik gdziekolwiek indziej (nawet wewnątrz clientWrapper, ale poza inputem)
      //    traktujemy jako klik “poza” i zamykamy popup.
      this.hideAutocomplete();
    }
    
  },
});

